/* eslint-disable consistent-return */
/* eslint-disable radix */
import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import * as validate from "@ownportal/customer-validations";
import i18n from "./localization/i18n";
import * as rk from "./localization/resourceKeys";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adobeDataLayer: any; // Or define a more specific type if you know the structure
  }
}

export const restrictKeyPressHandler = (regex: RegExp, length?: number) => {
  return (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    const stringCode = String.fromCodePoint(charCode);
    const resultantValue = e.target.value + stringCode;

    if (!regex.test(resultantValue)) {
      return e.preventDefault();
    }
    if (length && e.target.value.length === length) {
      return e.preventDefault();
    }

    return true;
  };
};

const FINGERPRINT_STORAGE_KEY = "vwfs.deviceFingerprint";
const FINGERPRINT_TTL_MS = 84 * 24 * 60 * 60 * 1000; // 12 weeks

export async function getFingerprint() {
  try {
    const stored = localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    if (stored) {
      const { value, expiresAt } = JSON.parse(stored);
      if (value && Date.now() < expiresAt) {
        return value;
      }
    }
  } catch {
    console.error('getFingerprint ===> localStorage unavailable error');
  }

  const fp = await FingerprintJS.load();
  const { visitorId } = await fp.get();

  try {
    localStorage.setItem(
      FINGERPRINT_STORAGE_KEY,
      JSON.stringify({ value: visitorId, expiresAt: Date.now() + FINGERPRINT_TTL_MS })
    );
  } catch {
    console.error('getFingerprint ===> localStorage write failed');
  }

  return visitorId;
}

export const transformDisplay = (val: string) => {
  // Strip all non numbers
  let displayVal = val.replace(/[^0-9|\\X]/g, "");
  displayVal = displayVal.substring(0, 9);

  // Inject dashes
  if (displayVal.length >= 4) {
    displayVal = `${displayVal.slice(0, 3)}-${displayVal.slice(3)}`;
  }

  if (displayVal.length >= 7) {
    displayVal = `${displayVal.slice(0, 6)}-${displayVal.slice(6)}`;
  }

  // Replace all numbers with 'X'
  if (displayVal.length <= 7) {
    displayVal = displayVal.replace(/[0-9]/g, "X");
  }
  return displayVal;
};

export const getFormattedSSN = (ssn: string): string => {
  if (ssn.length <= 2) {
    return ssn.replace(/./g, "X");
  }

  if (ssn.length <= 5) {
    return "XXX-XX".substring(0, ssn.length + 1);
  }

  const visibleDigits = ssn.substring(5);
  return `XXX-XX-${visibleDigits}`;
};

export const getChallenegeQuestionOptions = (brand: string) => {
  if (brand === "AUDI") {
    return [
      `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION1_AUDI)}`,
      `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION2_AUDI)}`,
      `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION3_AUDI)}`,
      `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION4_AUDI)}`,
      `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION5_AUDI)}`,
      `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION6_AUDI)}`,
      `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION7_AUDI)}`,
      `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION8_AUDI)}`,
      `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION9_AUDI)}`,
    ];
  }
  return [
    `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION1)}`,
    `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION2)}`,
    `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION3)}`,
    `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION4)}`,
    `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION5)}`,
    `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION6)}`,
    `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION7)}`,
    `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION8)}`,
    `${i18n.t(rk.TEXT_SECURITY_QUESTION_OPTION9)}`,
  ];
};

interface ReqContext {
  country?: string;
  correlationId?: string;
  Brand?: string;
  Source?: string;
  SourceApp?: string;
  ProcessName?: string;
  WidgetName?: string;
}

export const buildCommonPayloadRequest = (reqContext: ReqContext) => {
  const payload = {
    Country: `${reqContext?.country}` || "",
    CorrelationId: `${reqContext?.correlationId}` || "",
    Brand: `${reqContext?.Brand}` || "",
    Source: `${reqContext?.Source}` || "",
    SourceApp: `${reqContext?.SourceApp}` || "",
    ProcessName: `${reqContext?.ProcessName}` || "",
    ParentFunctionName: `${reqContext?.ProcessName}` || "",
    WidgetName: `${reqContext?.WidgetName}` || "",
  };
  return payload;
};

/* Mask email a*****a@email.com */
export const maskEmail = (email: string) => {
  const [name, domain] = email.split("@");
  const { length: len } = name;
  const maskedName = `${name[0]}*****${name[len - 1]}`;
  const maskedEmail = `${maskedName}@${domain}`;
  return maskedEmail;
};

export default async function getMaintenanceMode() {
  const host = window.location.origin;
  return axios
    .get(`${host}/public/maintenance.json`)
    .then((response) => {
      return response && response?.data
        ? response.data
        : { inMaintenanceMode: "N" };
    })
    .then((res) => {
      if (res.inMaintenanceMode === "Y") return true;
      return false;
    })
    .catch((e) => {
      console.error(e);
      return false;
    });
}

interface ShouldGoToNext {
  month: boolean;
  day: boolean;
  year: boolean;
}

const updateFocusAndState = (
  targetField: string,
  shouldGoToNext: any,
  field: string,
  setShouldGoToNext: any,
) => {
  const updatedShouldGoToNext = {
    ...shouldGoToNext,
    [field]: !shouldGoToNext[field],
  };
  if (shouldGoToNext[field]) {
    document.getElementById(`${targetField}-input`).focus();
  }
  setShouldGoToNext(updatedShouldGoToNext);
};

export const handleSpecialKeysDate = (
  event: KeyboardEvent,
  field: "month" | "day" | "year",
  day: string,
  year: string,
  shouldGoToNext: ShouldGoToNext,
  setShouldGoToNext: (value: ShouldGoToNext) => void,
) => {
  const caretPos = document.getElementById(`${field}-input`).selectionStart;
  const shouldGoTemp = shouldGoToNext;

  switch (event.key) {
    case "Backspace":
    case "Delete":
      if (field === "day") {
        if (day === "") {
          updateFocusAndState(
            "month",
            shouldGoToNext,
            field,
            setShouldGoToNext,
          );
        } else if (day === "0") {
          document.getElementById("day-input").focus();
        }
      } else if (field === "year") {
        if (year === "") {
          updateFocusAndState("day", shouldGoToNext, field, setShouldGoToNext);
        }
      }
      break;
    case "ArrowLeft":
      if (field === "day" && caretPos === 0) {
        updateFocusAndState("month", shouldGoToNext, field, setShouldGoToNext);
      } else if (field === "year" && caretPos === 0) {
        updateFocusAndState("day", shouldGoToNext, field, setShouldGoToNext);
      } else {
        shouldGoTemp[field] = false;
        setShouldGoToNext(shouldGoTemp);
      }
      break;
    case "ArrowRight":
      if (field === "month" && caretPos === 2) {
        updateFocusAndState("day", shouldGoToNext, field, setShouldGoToNext);
      } else if (field === "day" && caretPos === 2) {
        updateFocusAndState("year", shouldGoToNext, field, setShouldGoToNext);
      } else {
        shouldGoTemp[field] = false;
        setShouldGoToNext(shouldGoTemp);
      }
      break;
    default:
      break;
  }
};

export const handleOnChangeMonth = (event, setMonth) => {
  const { value } = event.target;
  const valueNumber = Number.parseInt(value, 10);

  if (valueNumber > 12) return;
  let newMonth;
  if (valueNumber > 1 && valueNumber < 10 && value.length < 2) {
    newMonth = `0${value}`;
    document.getElementById("day-input").focus();
  } else {
    newMonth = value;
  }

  if (newMonth === "00") return;

  setMonth(newMonth);

  if (value.length === 2) document.getElementById("day-input").focus();
};

export const handleOnChangeDay = (event, setDay) => {
  const { value } = event.target;
  const valueNumber = Number.parseInt(value, 10);

  if (valueNumber > 31) return;
  let newDay;
  if (valueNumber > 3 && valueNumber < 10 && value.length < 2) {
    newDay = `0${value}`;
    document.getElementById("year-input").focus();
  } else {
    newDay = value;
  }

  if (newDay === "00") return;

  setDay(newDay);

  if (value.length === 2) {
    document.getElementById("year-input").focus();
  }
};

export const handleOnKeyPressYear = (event) => {
  const { key } = event;
  if (!key.match(/^[0-9]*$/i)) return event.preventDefault();
};

export const isValidEmail = (email: string) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  return emailRegex.test(email);
};

export const isValidCanadaEmail = (email: string) => {
  const emailRegex =
    /^[a-zA-Z0-9.'&,ÀÁÂÄÇÈÉÊËÌÍÎÏÒÓÔÖàáâäçèéêëìíîïòóôöùúûüÙÚÛÜñÑ_%+-]+@[a-zA-Z0-9'&,ÀÁÂÄÇÈÉÊËÌÍÎÏÒÓÔÖàáâäçèéêëìíîïòóôöùúûüÙÚÛÜñÑ.-]+\.[a-zA-Z]{2,}$/;

  return emailRegex.test(email);
};

export const isValidAccountNumber = (accountNumber: string) => {
  return validate.isValidAccountNumber(accountNumber);
};

/**
 * Generate adobe analytics site load event.
 * @returns {void}
 */
export const generateSiteLoadEvent = () => {
  // Measure page load time using Performance API
  const loadTime =
    window.performance.timing.domContentLoadedEventEnd -
    window.performance.timing.navigationStart;

  // eslint-disable-next-line no-unused-vars
  const siteLoadEvent = {
    event: "PAGE_LOAD",
    page: {
      instanceId: {
        // defines which environment user is on ('QA' or 'Production')
        environment: process.env.REACT_APP_AWS_ENV,
      },
      info: {
        pageTitle: document.getElementsByTagName("title")[0]?.innerHTML,
        pageSubtitle: "", // first <h> element (if non pii)
        hostName: window.location.hostname, // URL Domain
        pagePath: window.location.pathname, // URL Page path
        urlQueryParameter: window.location.search, // URL Query Param (don't include PII)
        urlHashFragment: window.location.hash, // URL Hash Fragment
        pageLoadTime: loadTime, // Time it took for page to load
        pageID: "", // Numerical ID of page, empty since we don't have one.
        version: "", // Version of the page, empty since we don't have one.
        language: "en-us", // Language the Page is in
        // currency:
        //   account.Country === constants.COUNTRY_USA
        //     ? constants.USD_CURRENCY
        //     : constants.CANADIAN_CURRENCY,
        // market: account.Country, // respective market ('US' or 'CA')
        // brand: account.Brand, // Page Brand ('VW' or 'AUDI')
        // sourceId: getSourceId(),
      },
      category: {
        primaryCategory: "",
        subCategory: "Auth Portal", // sub-category of page, empthy since we don't have one.
        pageType: "AuthPortal", // type of page, empty for now?
      },
    },
    user: {
      testAccount: process.env.REACT_APP_AWS_ENV !== "prod", // either true for false
      partyId: "", // user's party ID - MUST BE SCRAMBLED/HASHED
      mcvid: "", // marketing cloud user ID to be stored in case of iframes
      accountId: "", // user's account ID - MUST BE SCRAMBLED/HASHED
      partyRoleType: "", // User's account type - 'Contract Holder' or other type
      paymentAccountType: "", // Type of payment system - Lease, loan, balloon*, etc
      logInState: false, // Either TRUE-logged in or FALSE-not logged in
      location: "", // Since we don't collect this data.
    },
    // Info that may be null
    finance: {
      autopayStatusOn: "", // If account has autopay on (TRUE) or not (FALSE) *
      balloonType: "",
      paperlessStatusOn: "", // If account has paperless on (TRUE) or not (FALSE)
      contractStart: "", // Contract Start Date
      contractEnd: "", // Contract End Date
      contractType: "", // Contract type such as loan, lease, or some other custom type such as balloon
      detailedAccountType: "",
      productName: "",
      currentBalance: "",
      principalRemaining: "", // Amount user still owes
      termLength: "", // Length of user's term
      paymentsRemaining: "", // Amount of payments user has remaining
      interestRate: "", // User's interest rate
      interestYearToDate: "", // User's interest year to date
      nextBillAmountOwed: "", // Amount owed at time of pageview
      nextBillDue: "", // Due date at time of pageview
      paymentFrequency: "", // Frequency of payments - monthly, weekly, daily
      paymentPastDue: "", // If payment is past due 'TRUE' or not 'FALSE
    },
    vehicle: {
      make: "", // Vehicle's make
      modelYear: "", // Vehicle's model year
      modelName: "", // Vehicle's model name
      vin: "", // Vehicle's VIN  - MUST BE SCRAMBLED/HASHED
      inventoryType: "", // Inventory type of users vehicle (will typically be 'new')
    },
    messages: {
      newMessageCount: 0, // Amount of new/unread messages - populates on homepage/message center
      totalMessageCount: 0, // Amount of messages user has - only in message center
    },
  };

  // Make sure the data layer exists in the window object.
  // Also check for route path to only cover dashboard and avoid triggering on help center etc.
  if (
    window.adobeDataLayer &&
    process.env.REACT_APP_ADOBE_ANALYTICS_FLAG === "true"
  )
    window.adobeDataLayer.push(siteLoadEvent);
};

export const linkClick = () => {
  const linkClickEvent = {};

  if (
    window.adobeDataLayer &&
    process.env.REACT_APP_ADOBE_ANALYTICS_FLAG === "true"
  )
    window.adobeDataLayer.push(linkClickEvent);
};
